import { isNativeApp, getPlatform } from './platform';
import { api } from '../api';

let deviceToken = null;
let listenersBound = false;
/** Resolvers waiting for device token after PushNotifications.register() */
const tokenWaiters = [];

function flushTokenWaiters(value) {
  while (tokenWaiters.length) {
    const fn = tokenWaiters.shift();
    try {
      fn(value);
    } catch (_) { /* ignore */ }
  }
}

/** Wait until native layer delivers a device token (or timeout). */
function waitForDeviceToken(timeoutMs = 25000) {
  if (deviceToken) return Promise.resolve(deviceToken);
  return new Promise((resolve) => {
    const t = setTimeout(() => resolve(deviceToken || null), timeoutMs);
    tokenWaiters.push((val) => {
      clearTimeout(t);
      resolve(val || deviceToken || null);
    });
  });
}

export async function initNativePush() {
  if (!isNativeApp()) return;

  const { PushNotifications } = await import('@capacitor/push-notifications');

  if (!listenersBound) {
    listenersBound = true;

    PushNotifications.addListener('registration', (token) => {
      deviceToken = token.value;
      flushTokenWaiters(token.value);
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.error('Push registration error:', err);
      flushTokenWaiters(null);
    });

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push received in foreground:', notification);
      // Tell the in-app notification badge to recompute right now instead of
      // waiting for the next 60s poll, so the user sees the dot the moment
      // their order goes ready/cancelled.
      try {
        window.dispatchEvent(new CustomEvent('app:notifications-refresh'));
      } catch (_) { /* ignore */ }
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action.notification.data;
      if (data) {
        const path = data.orderGroup
          ? `/confirmation/group/${data.orderGroup}`
          : data.orderNumber
            ? `/confirmation/${data.orderNumber}`
            : '/';
        window.location.href = path;
      }
    });
  }

  const permResult = await PushNotifications.requestPermissions();
  if (permResult.receive === 'granted') {
    await PushNotifications.register();
    // Register token with no orders so Admin can broadcast "orders open"
    // to every installed app, not only devices that already placed an order.
    const token = await waitForDeviceToken(15000);
    if (token) {
      try {
        await api.registerDevice(token, [], getPlatform());
      } catch (e) {
        console.warn('Native push: failed to register device for broadcasts:', e.message);
      }
    }
  }
}

export async function registerDeviceForOrders(orderIds) {
  if (!isNativeApp()) return false;

  const token = await waitForDeviceToken();
  if (!token) {
    console.warn('Native push: no device token yet — order push may not work until token arrives');
    return false;
  }

  try {
    await api.registerDevice(token, orderIds, getPlatform());
    return true;
  } catch (e) {
    console.error('Failed to register device for orders:', e);
    return false;
  }
}

export function hasDeviceToken() {
  return !!deviceToken;
}
